import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Colours from '@shared/ui/atoms/Colours';
import { FixedWhereabouts, WhereaboutsOption } from '@features/whereabouts/services/WhereaboutOptions';
import { whereaboutsToColour } from "@shared/utils/RandomColour";
import styled from "styled-components";
import { CustomToolTip } from "./CustomToolTip";
import { roundUpNearest10 } from "@shared/utils/MathUtils";
import { Checkbox } from '@shared/ui/atoms/Checkbox';
import { ColourCircle } from '@shared/ui/atoms/ColourCircle';
import { desktopSmall, desktopWide, fromMobile } from '@shared/ui/atoms/MediaQueries';

const DEFAULT_ACTIVE_KEYS = [FixedWhereabouts.Holiday, FixedWhereabouts.Office, FixedWhereabouts.Home];

// Picks which bars are shown by default. Matches on the stable `key` (HOLIDAY/OFFICE/HOME) rather
// than the company-configurable, localised `label`, then falls back to every option so the chart is
// never silently empty when a company has renamed or disabled the standard whereabouts.
export function computeDefaultActiveOptions(options: WhereaboutsOption[]): string[] {
  const defaults = options.filter(o => DEFAULT_ACTIVE_KEYS.includes(o.key)).map(o => o.label);
  return defaults.length > 0 ? defaults : options.map(o => o.label);
}

export function WhereaboutsSummaryBarChart(props: Props) {
  const {reportData, keyTableData} = props;
  const [activeOptions, setActiveOptions] = useState<string[]>([]);
  const initialised = useRef(false);
  const options = useMemo(() => {
    let opts = keyTableData.filter(wo => wo.enabled && wo.label && wo.key !== FixedWhereabouts.Unknown);
    return opts.sort((a, b) => a.required ? -1 : 1);
  }, [keyTableData]);

  // Default the visible bars from the actual loaded options (matched by stable key, not by
  // company-configurable/localised label). Falls back to all options when none of the standard
  // defaults are present, so the chart is never silently empty.
  useEffect(() => {
    if (initialised.current || options.length === 0) return;
    setActiveOptions(computeDefaultActiveOptions(options));
    initialised.current = true;
  }, [options]);

  // Scale the y-axis to the visible (stacked) bars only, so low-volume whereabouts like Sick
  // remain readable when selected on their own. Falls back to the all-options max while the
  // active set is still initialising.
  const maxVisibleYAxisValue = useMemo(() => {
    let highestValue = 0;
    reportData.forEach(row => {
      const sum = activeOptions.reduce((acc, label) => {
        const value = row[label];
        return acc + (typeof value === 'number' ? value : 0);
      }, 0);
      if (sum > highestValue) highestValue = sum;
    });
    return highestValue > 0 ? highestValue : props.maxValueOfYAxis;
  }, [reportData, activeOptions, props.maxValueOfYAxis]);

  const onToggleButtonClick = (value: WhereaboutsOption) => {
    if (activeOptions.includes(value.label)) {
      setActiveOptions(activeOptions.filter(si => si !== value.label))
    } else {
      setActiveOptions([...activeOptions, value.label])
    }
  }

  return (
    <>
      <div style={{height: 400}}>
        <ResponsiveContainer width="100%" height="100%" className={"whereaboutsSummaryBarChart"}>
          <BarChart
            width={500}
            height={400}
            data={reportData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid stroke={Colours.mildGrey} />

            <XAxis dataKey="name"
                   fontFamily={'proxima-nova'}
                   fill={Colours.darkGrey}
                   fontWeight={400}
                   fontSize={14}
                   xAxisId="0"
                   dy={4}
                   axisLine={false} />

            <XAxis dataKey="dateOfMonth"
                   fontFamily={'proxima-nova'}
                   fill={Colours.darkGrey}
                   fontWeight={700}
                   fontSize={14}
                   dy={-10}
                   xAxisId="1"
                   axisLine={false} />

            <YAxis fontFamily={'proxima-nova'}
                   fill={Colours.veryLightGrey}
                   fontSize={14}
                   axisLine={false}
                   allowDecimals={false}
                   domain={[0, roundUpNearest10(maxVisibleYAxisValue)]}  />

            <Tooltip content={<CustomToolTip title={"Whereabouts"} />} />

            {options.map((option, key) =>
              <Bar key={key}
                   dataKey={option.label}
                   hide={!activeOptions.includes(option.label)}
                   label={option.label}
                   stackId="a"
                   fill={whereaboutsToColour(options, option.key)} />)}

          </BarChart>

        </ResponsiveContainer>
      </div>

      <ToggleContainer>
        {options.map((value, key) => (
          <ToggleWrapper key={key}>
            <ColourCircle style={{marginRight: 12}} color={whereaboutsToColour(options, value.key)} />
            <Checkbox value={activeOptions.includes(value.label)}
                      onChange={() => onToggleButtonClick(value)}
                      text={value.label} />
          </ToggleWrapper>
        ))}
      </ToggleContainer>
    </>
  )
}

interface Props {
  reportData: any[];
  keyTableData: WhereaboutsOption[];
  maxValueOfYAxis: number;
}

const ToggleContainer = styled.div`
  & > * {
    margin: 0 0 12px 0;
    cursor: pointer;
    user-select: none;
  }
`

const ToggleWrapper = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  width: 100%;
  @media (${fromMobile}) {
    width: 50%;
  }
  @media (${desktopSmall}) {
    width: 33.3333%;
  }
  @media (${desktopWide}) {
    width: 25%;
  }
`
